import React, { useState, useCallback } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Upload, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import Papa from 'papaparse';

interface CSVUploadProps {
  onUploadComplete: (data: any) => void;
  onError: (error: string) => void;
}

interface CSVPreview {
  columns: string[];
  sampleRows: any[];
  rowCount: number;
}

export function CSVUpload({ onUploadComplete, onError }: CSVUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState<CSVPreview | null>(null);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleFile = useCallback((file: File) => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      onError('Please select a CSV file');
      return;
    }

    setIsUploading(true);
    setUploadStatus('idle');

    const reader = new FileReader();
    reader.onload = (e) => {
      const csvText = e.target?.result as string;
      const results = (Papa.parse as any)(csvText, {
        header: true,
        skipEmptyLines: true,
        preview: 10 // Only parse first 10 rows for preview
      });

      if (results.errors.length > 0) {
        onError(`CSV parsing error: ${results.errors[0].message}`);
        setUploadStatus('error');
        setIsUploading(false);
        return;
      }

      const columns = results.meta.fields || [];
      const sampleRows = results.data.slice(0, 5); // Show first 5 rows as preview
      
      setPreview({
        columns,
        sampleRows,
        rowCount: results.data.length
      });
      
      setIsUploading(false);
    };
    
    reader.onerror = () => {
      onError('Failed to read CSV file');
      setUploadStatus('error');
      setIsUploading(false);
    };
    
    reader.readAsText(file);
  }, [onError]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFile(files[0]);
    }
  }, [handleFile]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  }, [handleFile]);

  const handleUploadToBackend = async () => {
    if (!preview) return;

    setIsUploading(true);
    setUploadStatus('idle');

    try {
      // Create a FormData object to send the CSV file
      const formData = new FormData();
      const fileInput = document.getElementById('csv-file-input') as HTMLInputElement;
      if (fileInput?.files?.[0]) {
        formData.append('csv', fileInput.files[0]);
        formData.append('columns', JSON.stringify(preview.columns));
      }

      const token = localStorage.getItem('token');
      const response = await fetch('/api/data/upload-csv', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const result = await response.json();
      setUploadStatus('success');
      onUploadComplete(result);
    } catch (error: any) {
      onError(`Upload failed: ${error.message}`);
      setUploadStatus('error');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Upload Area */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Upload CSV Data
          </CardTitle>
          <CardDescription>
            Upload a CSV file to create a dynamic table in the backend. 
            The system will automatically detect the schema and create the appropriate table structure.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragging
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="text-lg font-medium mb-2">
              {isDragging ? 'Drop your CSV file here' : 'Drag & drop your CSV file here'}
            </p>
            <p className="text-gray-500 mb-4">or</p>
            <input
              id="csv-file-input"
              type="file"
              accept=".csv"
              onChange={handleFileInput}
              className="hidden"
            />
            <Button
              variant="outline"
              onClick={() => document.getElementById('csv-file-input')?.click()}
              disabled={isUploading}
            >
              {isUploading ? 'Processing...' : 'Choose CSV File'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      {preview && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              CSV Preview
            </CardTitle>
            <CardDescription>
              Detected {preview.columns.length} columns and {preview.rowCount} rows
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Columns */}
              <div>
                <h4 className="font-medium mb-2">Columns ({preview.columns.length}):</h4>
                <div className="flex flex-wrap gap-2">
                  {preview.columns.map((column, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm"
                    >
                      {column}
                    </span>
                  ))}
                </div>
              </div>

              {/* Sample Data */}
              <div>
                <h4 className="font-medium mb-2">Sample Data (first 5 rows):</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full border border-gray-200 rounded">
                    <thead className="bg-gray-50">
                      <tr>
                        {preview.columns.map((column, index) => (
                          <th key={index} className="px-3 py-2 text-left text-sm font-medium border-b">
                            {column}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.sampleRows.map((row, rowIndex) => (
                        <tr key={rowIndex} className="border-b">
                          {preview.columns.map((column, colIndex) => (
                            <td key={colIndex} className="px-3 py-2 text-sm border-r">
                              {row[column] || '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Upload Button */}
              <div className="flex justify-end">
                <Button
                  onClick={handleUploadToBackend}
                  disabled={isUploading}
                  className="flex items-center gap-2"
                >
                  {isUploading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Upload to Backend
                    </>
                  )}
                </Button>
              </div>

              {/* Status */}
              {uploadStatus === 'success' && (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="w-5 h-5" />
                  <span>CSV uploaded successfully! Table created in backend.</span>
                </div>
              )}
              {uploadStatus === 'error' && (
                <div className="flex items-center gap-2 text-red-600">
                  <AlertCircle className="w-5 h-5" />
                  <span>Upload failed. Please try again.</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
